import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Column,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_RowSelectionState,
  type MRT_SortingState,
  type MRT_Updater,
} from 'material-react-table';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import { format } from 'date-fns';
import IHubAdapterService from '../../../Services/IHubAdapterService';
import Notification from '../../Layout/Notification';
import DeferredMessageDetails from './DeferredMessageDetails';
import './IHubAdapter.scss';

const MESSAGE_TYPE_OPTIONS = [
  'SERVICE',
  'REMINDER',
  'ORDER_SENT',
  'ORDER_ACCEPTED',
  'INVOICED_ITEMS',
  'SERVICE_INSTRUCTIONS',
];

export interface DeferredMessageRow {
  id: string;
  sequenceNumber: number | null;
  messageId: string | null;
  serviceId: string | null;
  eventType: string | null;
  messageType: string | null;
  createdDate: string | null;
  processedDate: string | null;
  processed: boolean;
  expired: boolean;
  deferredReason: string | null;
  errorCode: number | null;
  description: string | null;
}

type PillVariant = 'success' | 'warning' | 'danger' | 'neutral';

const StatusPill = ({ label, variant }: { label: string; variant: PillVariant }) => (
  <span className={`ihub-pill ihub-pill-${variant}`}>{label}</span>
);

// Mirrors com.cpaglobal.ihubadapter.model.dto.ihub.response.DeferredReason
const REASON_META: Record<string, { label: string; variant: PillVariant }> = {
  MISSING_SERVICE: { label: 'Missing service', variant: 'danger' },
  CONFLICT: { label: 'Conflict', variant: 'warning' },
  OTHER: { label: 'Other', variant: 'neutral' },
};

const ReasonPill = ({ reason }: { reason: string | null }) => {
  if (!reason) return <span>-</span>;
  const meta = REASON_META[reason] ?? { label: reason, variant: 'neutral' as PillVariant };
  return <StatusPill label={meta.label} variant={meta.variant} />;
};

const MonoCell = ({ value }: { value: unknown }) => {
  const text = value === null || value === undefined || value === '' ? '-' : String(value);
  return <span className="ihub-dm-mono">{text}</span>;
};

const formatDateCell = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return format(parsed, 'dd MMM yyyy, HH:mm:ss');
};

// Spring binds LocalDateTime query params from ISO_LOCAL_DATE_TIME (no zone / no `Z`).
const toLocalDateTimeParam = (value: unknown): string | undefined => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return undefined;
  return format(value, "yyyy-MM-dd'T'HH:mm:ss");
};

type DateRangeValue = [Date | undefined, Date | undefined];

const validOrUndefined = (value: Date | null | undefined): Date | undefined =>
  value instanceof Date && !Number.isNaN(value.getTime()) ? value : undefined;

const timestampOf = (value: Date | undefined): number | null => (value ? value.getTime() : null);

const formatRangeLabel = (from: Date | undefined, to: Date | undefined): string => {
  const fmt = (d: Date) => format(d, 'dd MMM, HH:mm');
  if (from && to) return `${fmt(from)} - ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  if (to) return `Until ${fmt(to)}`;
  return '';
};

const CreatedRangeFilter = ({ column }: { column: MRT_Column<DeferredMessageRow> }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);
  const [draftTo, setDraftTo] = useState<Date | null>(null);

  const filterValue = (column.getFilterValue() as DateRangeValue | undefined) ?? [
    undefined,
    undefined,
  ];
  const from = validOrUndefined(filterValue[0]);
  const to = validOrUndefined(filterValue[1]);
  const label = formatRangeLabel(from, to);

  const openPopover = (target: HTMLElement) => {
    setDraftFrom(from ?? null);
    setDraftTo(to ?? null);
    setAnchorEl(target);
  };

  const commit = (nextFromRaw: Date | null, nextToRaw: Date | null) => {
    const nextFrom = validOrUndefined(nextFromRaw);
    const nextTo = validOrUndefined(nextToRaw);
    const unchanged =
      timestampOf(nextFrom) === timestampOf(from) && timestampOf(nextTo) === timestampOf(to);
    if (unchanged) return;
    column.setFilterValue(nextFrom || nextTo ? [nextFrom, nextTo] : undefined);
  };

  // Closing the popover applies the draft, so no picked value is ever lost.
  const closeAndCommit = () => {
    commit(draftFrom, draftTo);
    setAnchorEl(null);
  };

  const clear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    commit(null, null);
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        className="ihub-dm-filter ihub-dm-rangefield"
        placeholder="Filter by date"
        value={label}
        title={label || undefined}
        onClick={e => openPopover(e.currentTarget)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPopover(e.currentTarget);
          }
        }}
        slotProps={{
          htmlInput: { readOnly: true, 'aria-label': 'Filter by created date range' },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {(from || to) && (
                  <IconButton
                    size="small"
                    aria-label="Clear created date filter"
                    onClick={e => {
                      e.stopPropagation();
                      clear();
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <CalendarMonthIcon fontSize="small" className="ihub-dm-rangefield-cal" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeAndCommit}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'ihub-dm-range-paper' } }}
      >
        <div className="ihub-dm-range-pop">
          <DateTimePicker
            label="From"
            value={draftFrom}
            onChange={setDraftFrom}
            format="MM/dd/yyyy HH:mm"
            ampm={false}
            maxDateTime={validOrUndefined(draftTo)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DateTimePicker
            label="To"
            value={draftTo}
            onChange={setDraftTo}
            format="MM/dd/yyyy HH:mm"
            ampm={false}
            minDateTime={validOrUndefined(draftFrom)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <div className="ihub-dm-range-actions">
            <Button size="small" className="ihub-dm-range-clear" onClick={clear}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              className="ihub-dm-range-apply"
              onClick={closeAndCommit}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

function filtersToParams(columnFilters: MRT_ColumnFiltersState): Record<string, string> {
  const params: Record<string, string> = {};
  for (const { id, value } of columnFilters) {
    switch (id) {
      case 'serviceId':
      case 'eventType':
      case 'messageId':
      case 'sequenceNumber':
      case 'messageType':
      case 'deferredReason': {
        const v = String(value ?? '').trim();
        if (v) params[id] = v;
        break;
      }
      case 'processed': {
        if (value === 'Processed') params.processed = 'true';
        else if (value === 'Pending') params.processed = 'false';
        break;
      }
      case 'expired': {
        if (value === 'Expired') params.expired = 'true';
        else if (value === 'Valid') params.expired = 'false';
        break;
      }
      case 'createdDate': {
        const [from, to] = Array.isArray(value) ? value : [undefined, undefined];
        const after = toLocalDateTimeParam(from);
        const before = toLocalDateTimeParam(to);
        if (after) params.createdAfter = after;
        if (before) params.createdBefore = before;
        break;
      }
      case 'processedDate': {
        const [from, to] = Array.isArray(value) ? value : [undefined, undefined];
        const after = toLocalDateTimeParam(from);
        const before = toLocalDateTimeParam(to);
        if (after) params.processedAfter = after;
        if (before) params.processedBefore = before;
        break;
      }
    }
  }
  return params;
}

interface DeferredMessagesProps {
  onMount?: (actions: {
    toggleFilters: () => void;
    toggleFullScreen: () => void;
    copySelectedServiceIds: () => void;
  }) => void;
  onSelectionChange?: (count: number, copyableCount: number) => void;
}

const DeferredMessages = ({ onMount, onSelectionChange }: DeferredMessagesProps) => {
  const [rows, setRows] = useState<DeferredMessageRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [selectedMessage, setSelectedMessage] = useState<DeferredMessageRow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectionSummary, setSelectionSummary] = useState({ selected: 0, copyable: 0 });

  // Maps row id -> serviceId across loaded pages so a selection survives paging.
  const serviceIdByIdRef = useRef<Record<string, string>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingFilterUpdatersRef = useRef<MRT_Updater<MRT_ColumnFiltersState>[]>([]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = filtersToParams(columnFilters);
      const response = await IHubAdapterService.findDeferredMessages({
        page,
        size: rowsPerPage,
        sort: `${sortBy},${sortOrder}`,
        ...filterParams,
      });
      setRows(response.data?.content ?? []);
      setTotalRows(response.data?.totalElements ?? 0);
    } catch (err) {
      Notification.fromError(err, { fallbackTitle: "Couldn't load deferred messages" });
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, columnFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    rows.forEach(r => {
      if (r.id) serviceIdByIdRef.current[r.id] = r.serviceId ?? '';
    });
    const ids = Object.keys(rowSelection).filter(id => rowSelection[id]);
    const copyable = new Set(ids.map(id => serviceIdByIdRef.current[id]).filter(Boolean)).size;
    setSelectionSummary(prev =>
      prev.selected === ids.length && prev.copyable === copyable
        ? prev
        : { selected: ids.length, copyable }
    );
    onSelectionChange?.(ids.length, copyable);
  }, [rows, rowSelection, onSelectionChange]);

  const handleColumnFiltersChange = useCallback(
    (updaterOrValue: MRT_Updater<MRT_ColumnFiltersState>) => {
      pendingFilterUpdatersRef.current.push(updaterOrValue);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const pending = pendingFilterUpdatersRef.current;
        pendingFilterUpdatersRef.current = [];
        setColumnFilters(prev =>
          pending.reduce<MRT_ColumnFiltersState>(
            (acc, updater) => (typeof updater === 'function' ? updater(acc) : updater),
            prev
          )
        );
        setPage(0);
      }, 350);
    },
    []
  );

  const copySelectedServiceIds = useCallback(async () => {
    const ids = Object.keys(rowSelection).filter(id => rowSelection[id]);
    const serviceIds = Array.from(
      new Set(ids.map(id => serviceIdByIdRef.current[id]).filter((s): s is string => Boolean(s)))
    );
    if (serviceIds.length === 0) {
      Notification.showWarn('No service IDs to copy for the selected rows.');
      return;
    }
    try {
      await navigator.clipboard.writeText(serviceIds.join('\n'));
      Notification.showSuccess(
        `Copied ${serviceIds.length} service ID${serviceIds.length === 1 ? '' : 's'} to clipboard.`
      );
    } catch {
      Notification.showError('Could not copy to clipboard.');
    }
  }, [rowSelection]);

  const columns = useMemo<MRT_ColumnDef<DeferredMessageRow>[]>(
    () => [
      {
        accessorKey: 'serviceId',
        header: 'Service ID',
        size: 150,
        filterVariant: 'text',
        Cell: ({ cell }) => <MonoCell value={cell.getValue()} />,
      },
      {
        accessorKey: 'messageType',
        header: 'Message Type',
        size: 170,
        filterVariant: 'select',
        filterSelectOptions: MESSAGE_TYPE_OPTIONS,
      },
      {
        accessorKey: 'deferredReason',
        header: 'Reason',
        size: 150,
        filterVariant: 'select',
        filterSelectOptions: [
          { label: 'Missing service', value: 'MISSING_SERVICE' },
          { label: 'Conflict', value: 'CONFLICT' },
          { label: 'Other', value: 'OTHER' },
        ],
        Cell: ({ row }) => <ReasonPill reason={row.original.deferredReason} />,
      },
      {
        accessorKey: 'messageId',
        header: 'Message ID',
        size: 180,
        filterVariant: 'text',
        Cell: ({ cell }) => <MonoCell value={cell.getValue()} />,
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        size: 180,
        Filter: ({ column }) => <CreatedRangeFilter column={column} />,
        Cell: ({ cell }) => formatDateCell(cell.getValue<string | null>()),
      },
      {
        accessorKey: 'processed',
        header: 'Status',
        size: 120,
        enableSorting: false,
        filterVariant: 'select',
        filterSelectOptions: ['Processed', 'Pending'],
        Cell: ({ row }) =>
          row.original.processed ? (
            <StatusPill label="Processed" variant="success" />
          ) : (
            <StatusPill label="Pending" variant="warning" />
          ),
      },
      {
        accessorKey: 'expired',
        header: 'Expiry',
        size: 110,
        enableSorting: false,
        filterVariant: 'select',
        filterSelectOptions: ['Expired', 'Valid'],
        Cell: ({ row }) =>
          row.original.expired ? (
            <StatusPill label="Expired" variant="danger" />
          ) : (
            <StatusPill label="Valid" variant="neutral" />
          ),
      },
    ],
    []
  );

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === 'desc' }],
    [sortBy, sortOrder]
  );

  const table = useMaterialReactTable({
    data: rows,
    columns,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalRows,
    layoutMode: 'grid',
    enableRowSelection: true,
    getRowId: row => row.id,
    positionToolbarAlertBanner: 'none',
    enableColumnActions: false,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableStickyHeader: true,
    enableTopToolbar: true,
    enableToolbarInternalActions: false,
    renderTopToolbar: ({ table }) =>
      table.getState().isFullScreen ? (
        <div className="ihub-dm-fsbar">
          {selectionSummary.selected > 0 && (
            <Tooltip
              title={
                selectionSummary.copyable > 0
                  ? 'Copy selected service IDs'
                  : 'Selected rows have no service IDs to copy'
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  className="ihub-dm-copy-btn"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  disabled={selectionSummary.copyable === 0}
                  onClick={copySelectedServiceIds}
                >
                  Copy IDs ({selectionSummary.copyable})
                </Button>
              </span>
            </Tooltip>
          )}
          <button
            type="button"
            className="ihub-dm-fsbtn"
            onClick={() => table.setShowColumnFilters(prev => !prev)}
          >
            <FilterListIcon fontSize="small" /> Filters
          </button>
          <button
            type="button"
            className="ihub-dm-fsexit"
            onClick={() => table.setIsFullScreen(false)}
          >
            ✕ Exit fullscreen
          </button>
        </div>
      ) : (
        false
      ),
    columnFilterDisplayMode: 'subheader',
    initialState: { density: 'compact', showColumnFilters: false },
    state: {
      sorting,
      columnFilters,
      rowSelection,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
      isLoading: loading,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onRowSelectionChange: setRowSelection,
    onSortingChange: updater => {
      const next = (typeof updater === 'function' ? updater(sorting) : updater) as MRT_SortingState;
      if (next[0]) {
        setSortBy(next[0].id);
        setSortOrder(next[0].desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: updater => {
      const prev: MRT_PaginationState = { pageIndex: page, pageSize: rowsPerPage };
      const next = (typeof updater === 'function' ? updater(prev) : updater) as MRT_PaginationState;
      setPage(next.pageIndex);
      setRowsPerPage(next.pageSize);
    },
    paginationDisplayMode: 'pages',
    muiTableBodyRowProps: ({ row }) => ({
      role: 'button',
      tabIndex: 0,
      onClick: () => {
        setSelectedMessage(row.original);
        setDetailOpen(true);
      },
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setSelectedMessage(row.original);
          setDetailOpen(true);
        }
      },
      className:
        row.original.deferredReason === 'MISSING_SERVICE' ? 'ihub-dm-row-alert' : undefined,
    }),
    muiTablePaperProps: { elevation: 0, className: 'ihub-dm-paper' },
    muiTableContainerProps: { className: 'ihub-dm-table-container' },
    muiTableHeadCellProps: { className: 'ihub-dm-head' },
    muiTableBodyCellProps: { className: 'ihub-dm-cell' },
    muiFilterTextFieldProps: { variant: 'outlined', size: 'small', className: 'ihub-dm-filter' },
    muiBottomToolbarProps: { className: 'ihub-dm-bottom' },
    muiSelectCheckboxProps: {
      onClick: e => e.stopPropagation(),
      onKeyDown: e => e.stopPropagation(),
    },
    muiSelectAllCheckboxProps: {
      onClick: e => e.stopPropagation(),
    },
  });

  useEffect(() => {
    onMount?.({
      toggleFilters: () => table.setShowColumnFilters(prev => !prev),
      toggleFullScreen: () => table.setIsFullScreen(prev => !prev),
      copySelectedServiceIds,
    });
  }, [onMount, table, copySelectedServiceIds]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div id="ihub-deferred-messages-container" className="ihub-dm-container">
        <MaterialReactTable table={table} />
        <DeferredMessageDetails
          open={detailOpen}
          message={selectedMessage}
          onClose={() => setDetailOpen(false)}
        />
      </div>
    </LocalizationProvider>
  );
};

export default DeferredMessages;
